import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  Outlet,
} from 'react-router';
import { useEffect } from 'react';
import { ThemeProvider } from '@/lib/theme-provider';
import { Layout } from '@/components/layout/layout';
import { PoliciesPage } from '@/pages/policies';
import { TransactionsPage } from '@/pages/transactions';
import { MembersPage } from '@/pages/members';
import { SettingsPage } from '@/pages/settings';
import { GroupsPage } from '@/pages/groups';
import { AccountsPage } from '@/pages/accounts';
import { AccountDetailPage } from '@/pages/accounts/detail';
import { AddressBookPage } from '@/pages/address-book';
import { SignupPage } from '@/pages/signup';
import ForgotPasswordPage from '@/pages/forgot-password';
import ResetPasswordPage from '@/pages/reset-password';
import { RegisterDevicePage } from '@/pages/register-device';
import { LayoutOnboarding } from './components/layout/layout-onboarding';
import './App.css';
import { RequireAnon } from '@/components/auth/RequireAnon';
import { RequireSessionOnly } from '@/components/auth/RequireSessionOnly';
import { RequireFullAuthWithMobile } from '@/components/auth/RequireFullAuthWithMobile';
import { RequireOrgHasAccounts } from '@/components/auth/RequireOrgHasAccounts';
import { RequireOrgHasNoAccounts } from '@/components/auth/RequireOrgHasNoAccounts';
import WaitingApprovalPage from '@/pages/waiting-approval';
import SignInPage from './pages/signin';
import { CreateOrganizationPage } from './pages/create-organization';
import { CreateFirstAccountPage } from './pages/create-first-account';
import OrganizationRedirectPage from './pages/organization-redirect';
import { TransactionBuilderPage } from './pages/new-transaction';
import { useRestrictedRegionGate } from '@/hooks/use-restricted-region-gate';
import { UnsupportedRegionPage } from '@/components/compliance/UnsupportedRegionPage';
import { getMobileStoreUrl } from '@/utils/mobileStoreRedirect';
import { ExecutionProvider } from '@/components/execution/execution-provider';
import { useAuthClient } from '@/lib/authClient';
import { useMyOrganization } from '@/hooks/organization/use-my-organization';

/**
 * Keeps transient proposal execution state within one authenticated
 * user/organization boundary. A changed identity remounts the provider so an
 * execution from a previous session cannot appear in the next one.
 */
function ScopedExecutionProvider() {
  const { user } = useAuthClient();
  const { organization } = useMyOrganization();

  // Wait for a complete identity so the provider never remounts mid-execution
  // as organization data transitions from loading to resolved.
  if (!user || !organization) {
    return null;
  }

  const executionScopeKey = `${user.id}:${organization.id}`;

  return (
    <ExecutionProvider key={executionScopeKey}>
      <Outlet />
    </ExecutionProvider>
  );
}

/**
 * Owns the normal router tree once the compliance gate has allowed the app to mount.
 */
function RoutedApp() {
  return (
    <Routes>
      {/* 1) Anon-only: signin/signup */}
      <Route element={<RequireAnon />}>
        {/* Full-bleed 2-column layout; owns its own chrome (no LayoutOnboarding). */}
        <Route path="/signin" element={<SignInPage />} />
        <Route
          path="/signup"
          element={
            <LayoutOnboarding>
              <SignupPage />
            </LayoutOnboarding>
          }
        />
        <Route
          path="/forgot-password"
          element={
            <LayoutOnboarding>
              <ForgotPasswordPage />
            </LayoutOnboarding>
          }
        />
        <Route
          path="/reset-password"
          element={
            <LayoutOnboarding>
              <ResetPasswordPage />
            </LayoutOnboarding>
          }
        />
      </Route>

      {/* 2) Session required: device registration, auth-loading */}
      <Route element={<RequireSessionOnly />}>
        <Route
          path="/organization-redirect"
          element={
            <LayoutOnboarding>
              <OrganizationRedirectPage />
            </LayoutOnboarding>
          }
        />
        <Route
          path="/create-organization"
          element={
            <LayoutOnboarding>
              <CreateOrganizationPage />
            </LayoutOnboarding>
          }
        />
        <Route
          path="/register-device"
          element={
            <LayoutOnboarding>
              <RegisterDevicePage />
            </LayoutOnboarding>
          }
        />
      </Route>

      {/* 3) Mobile connection required: waiting page */}
      <Route element={<RequireFullAuthWithMobile />}>
        <Route
          path="/waiting-approval"
          element={
            <LayoutOnboarding>
              <WaitingApprovalPage />
            </LayoutOnboarding>
          }
        />
      </Route>

      {/* 4) Org membership required + must have NO accounts: create first account */}
      <Route element={<RequireOrgHasNoAccounts />}>
        <Route
          path="/create-first-account"
          element={
            <LayoutOnboarding>
              <CreateFirstAccountPage />
            </LayoutOnboarding>
          }
        />
      </Route>

      {/* 5) Org membership + at least one account required: main app routes */}
      <Route element={<RequireOrgHasAccounts />}>
        <Route element={<ScopedExecutionProvider />}>
          <Route
            path="/policies"
            element={
              <Layout>
                <PoliciesPage />
              </Layout>
            }
          />
          <Route
            path="/transactions"
            element={
              <Layout>
                <TransactionsPage />
              </Layout>
            }
          />
          <Route
            path="/accounts"
            element={
              <Layout>
                <AccountsPage />
              </Layout>
            }
          />
          <Route
            path="/accounts/:accountId"
            element={
              <Layout>
                <AccountDetailPage />
              </Layout>
            }
          />
          <Route
            path="/members"
            element={
              <Layout>
                <MembersPage />
              </Layout>
            }
          />
          <Route
            path="/address-book"
            element={
              <Layout>
                <AddressBookPage />
              </Layout>
            }
          />
          <Route
            path="/settings"
            element={<Navigate to="/settings/organization" replace />}
          />
          <Route
            path="/settings/organization"
            element={
              <Layout>
                <SettingsPage />
              </Layout>
            }
          />
          <Route
            path="/settings/personal"
            element={
              <Layout>
                <SettingsPage />
              </Layout>
            }
          />
          <Route
            path="/groups"
            element={
              <Layout>
                <GroupsPage />
              </Layout>
            }
          />
          <Route path="/new-transaction" element={<TransactionBuilderPage />} />
        </Route>
      </Route>

      <Route path="/" element={<Navigate to="/transactions" replace />} />
    </Routes>
  );
}

/**
 * Installs the browser-history router around the route tree and its
 * route-scoped providers.
 */
function AppRoutes() {
  return (
    <Router>
      <RoutedApp />
    </Router>
  );
}

/**
 * Applies the restricted-region startup gate before rendering the main router tree.
 */
function App() {
  const storeRedirectUrl = getMobileStoreUrl(window.navigator.userAgent);
  const { isBlocked, isChecking } = useRestrictedRegionGate();

  useEffect(() => {
    if (storeRedirectUrl) {
      window.location.replace(storeRedirectUrl);
    }
  }, [storeRedirectUrl]);

  let content;

  if (storeRedirectUrl) {
    // Case: mobile browsers should install/open Den rather than use the web app.
    content = <div className="min-h-screen bg-white" />;
  } else if (isBlocked) {
    // Case: the backend has already determined this client is in a restricted region.
    content = <UnsupportedRegionPage />;
  } else if (isChecking) {
    // Case: keep the router hidden while the startup auth/compliance probe is still in flight.
    content = <div className="min-h-screen bg-white" />;
  } else {
    // Case: the startup gate passed, so render the normal application routes.
    content = <AppRoutes />;
  }

  return <ThemeProvider defaultTheme="light">{content}</ThemeProvider>;
}

export default App;
